import _ from 'underscore';
import React, { useCallback, useContext, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { AdsCampaignDisclaimers, DisclaimerStringMap } from '@bingads-webui-campaign-react/ads-campaign-disclaimers';
import { DisclaimerAdExtension } from '@bingads-webui/campaign-odata-schemas';
import DisclaimerRepository from '@bingads-webui-campaign-legacy/adextension-shared/service/repository';
import { uiblockerStyle } from '@bingads-webui-campaign-react/common-styles';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getDisclaimerPanelStyles, getDisclaimerSettingsStyles } from './styles/disclaimer-styles';

const getStyles = (components, theme) => ({
  disclaimerContainer: {
    ...getDisclaimerSettingsStyles(theme),
    '& .ba-pagination': {
      '& .pager-input': {
        background: '#DCDCDC',
      },
    },
    '& .focus-style': {
      border: '1px solid black',
    },
    '& input': {
      outline: '0 !important',
    }
  },
  '@global': {
    ...getDisclaimerPanelStyles(theme),
    ...uiblockerStyle(theme),
  },
});

const convert2ViewModel = (data, originalSelectedDisclaimer) => {
  const { DisclaimerAdsEnabled: disclaimerAdsEnabled, AdExtensionAssociations: adExtensionAssociations } = data;
  let selectedDisclaimer = null;
  if (adExtensionAssociations && adExtensionAssociations.length > 0) {
    const activeAssociations = adExtensionAssociations.filter(association => association.Status === 'Active');
    if (activeAssociations && activeAssociations.length > 0) {
      const activeAssociation = activeAssociations[0];
      selectedDisclaimer = {
        Id: activeAssociation.Id,
        Name: activeAssociation.Name,
      };
    }
  }

  return {
    DisclaimerAdsEnabled: disclaimerAdsEnabled,
    disclaimer: { originalSelectedDisclaimer, selectedDisclaimer, adExtensionAssociations },
  };
};

const convert2LogData = (data) => {
  const { DisclaimerAdsEnabled: disclaimerAdsEnabled, disclaimer } = data || {};
  const logData = {
    Enabled: disclaimerAdsEnabled,
  };
  if (disclaimerAdsEnabled && disclaimer && disclaimer.selectedDisclaimer && disclaimer.selectedDisclaimer.Id > 0) {
    logData.DisclaimerId = disclaimer.selectedDisclaimer.Id;
  }
  return logData;
};

const Disclaimer = ({
  campaignId,
  validationProps,
  updateValidation,
}) => {
  const { data: { disclaimerAdsEnabled, disclaimer } } = validationProps;
  const { originalSelectedDisclaimer } = disclaimer || {};
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap, DisclaimerStringMap]), [i18n]);
  const classes = useDefaultStyles(getStyles);
  const permissions = useContext(PermissionsContext);
  const activity = useContext(ActivityContext);
  const [logData, setLogData] = useState(null);

  const disclaimerRepositoryRef = useRef(new DisclaimerRepository({
    schema: DisclaimerAdExtension,
    skipExtensionCache: true,
  }));

  const disclaimerRef = useRef(null);
  const onDataChange = useCallback(() => {
    const result = disclaimerRef.current.getValues();
    const error = disclaimerRef.current.validateAndWarn();
    const data = convert2ViewModel(result, originalSelectedDisclaimer);
    updateValidation(error ? [error] : [], data);

    if (activity) {
      const newLogData = convert2LogData(data);
      // This is to avoid unnecessary signal calls, e.g., when toggling the disclaimer as false, there are multiple calls to onDataChange
      if (!_.isEqual(logData, newLogData)) {
        setLogData(newLogData);
        activity.signal(`onDisclaimerChange - ${JSON.stringify(newLogData)}`);
      }
    }
  }, [activity, logData, originalSelectedDisclaimer, updateValidation]);

  // this is to ensure that the disclaimer is not rendered if initial data is not available
  if (!disclaimer) {
    return null;
  }

  return (
    <div className={classNames(classes.disclaimerContainer, 'disclaimer-container')}>
      <AdsCampaignDisclaimers
        ref={disclaimerRef}
        DisclaimerAdsEnabled={disclaimerAdsEnabled}
        selectedDisclaimer={originalSelectedDisclaimer}
        onDataChange={onDataChange}
        campaignId={campaignId > 0 ? campaignId : 0}
        repository={disclaimerRepositoryRef.current}
        i18n={legacyI18n}
        permissions={permissions}
        schema={DisclaimerAdExtension}
        AllowDisclaimerEnabled
        usePanel
        disableTitle
        isForNew
        enableRandomPage={false}
      />
    </div>
  );
};

Disclaimer.propTypes = {
  campaignId: PropTypes.number,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      disclaimerAdsEnabled: PropTypes.bool,
      disclaimer: PropTypes.shape({
        selectedDisclaimer: PropTypes.shape({
          Id: PropTypes.number,
          Name: PropTypes.string,
        }),
        adExtensionAssociations: PropTypes.arrayOf(PropTypes.shape({
          Id: PropTypes.number,
          Name: PropTypes.string,
          Status: PropTypes.string,
        })),
      }),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
};

Disclaimer.defaultProps = {
  campaignId: null,
};

export const ValidatedDisclaimer = withError(withCustomValidation(Disclaimer));
